import type { AppLocale } from '~/constants/appLocale'

/**
 * The URLs of the marketing pages in Czech, German and Polish: /pricing is
 * /cs/cenik, /de/preise and /pl/cennik, /resume-examples/teacher is
 * /cs/priklady-zivotopisu/ucitel.
 *
 * Code keeps using the English path everywhere (links, JSON-LD, the sitemap);
 * constants/appLocale.ts translates it with localizedPath() and back with
 * defaultLocalePath(). Each page also declares its own paths in a literal
 * `defineI18nRoute({ paths })`, because @nuxtjs/i18n reads only literals;
 * tests/i18n/localizedRoutes.test.ts keeps the two in step.
 *
 * A URL here is a promise to search engines: change one only together with a
 * redirect from the old one (server/middleware/localizedRedirect.ts sends the
 * English slugs under /cs and /de, the URLs before 2026-10, to these).
 */
export type TranslatedLocale = Exclude<AppLocale, 'en'>

type Translations = Record<TranslatedLocale, string>

/** English path → its Czech, German and Polish path, without the /cs, /de or /pl prefix. */
export const LOCALIZED_PAGES: Record<string, Translations> = {
  '/affiliate': { cs: '/partnersky-program', de: '/partnerprogramm', pl: '/program-partnerski' },
  '/affiliate/terms': { cs: '/partnersky-program/podminky', de: '/partnerprogramm/bedingungen', pl: '/program-partnerski/regulamin' },
  '/affiliate-programs': { cs: '/affiliate-programy', de: '/affiliate-programme', pl: '/programy-afiliacyjne' },
  '/ai-cover-letter': { cs: '/motivacni-dopis', de: '/anschreiben', pl: '/list-motywacyjny' },
  '/ai-features': { cs: '/ai-funkce', de: '/ki-funktionen', pl: '/funkcje-ai' },
  '/alternatives': { cs: '/alternativy', de: '/alternativen', pl: '/alternatywy' },
  '/ats-check': { cs: '/ats-kontrola-zivotopisu', de: '/ats-check-lebenslauf', pl: '/sprawdzanie-cv-ats' },
  '/ats-friendly-resume-template': { cs: '/ats-sablona-zivotopisu', de: '/ats-lebenslauf-vorlage', pl: '/szablon-cv-ats' },
  '/contact': { cs: '/kontakt', de: '/kontakt', pl: '/kontakt' },
  '/cv-analytics': { cs: '/statistiky-zivotopisu', de: '/lebenslauf-statistiken', pl: '/statystyki-cv' },
  '/cv-languages': { cs: '/preklad-zivotopisu', de: '/lebenslauf-uebersetzen', pl: '/tlumaczenie-cv' },
  '/demo': { cs: '/ukazka', de: '/demo', pl: '/demo' },
  '/free-cv-builder': { cs: '/zivotopis-zdarma', de: '/lebenslauf-kostenlos', pl: '/kreator-cv-za-darmo' },
  '/how-long-should-a-resume-be': { cs: '/jak-dlouhy-ma-byt-zivotopis', de: '/wie-lang-sollte-ein-lebenslauf-sein', pl: '/jak-dlugie-powinno-byc-cv' },
  '/how-to-prepare-for-an-interview': { cs: '/jak-se-pripravit-na-pohovor', de: '/vorbereitung-vorstellungsgespraech', pl: '/jak-przygotowac-sie-do-rozmowy-kwalifikacyjnej' },
  '/how-to-write-a-resume': { cs: '/jak-napsat-zivotopis', de: '/lebenslauf-schreiben', pl: '/jak-napisac-cv' },
  '/job-application-tracker': { cs: '/prehled-pracovnich-prihlasek', de: '/bewerbungstracker', pl: '/organizer-aplikacji-o-prace' },
  '/online-cv': { cs: '/online-zivotopis', de: '/online-lebenslauf', pl: '/cv-online' },
  '/pricing': { cs: '/cenik', de: '/preise', pl: '/cennik' },
  '/privacy': { cs: '/ochrana-osobnich-udaju', de: '/datenschutz', pl: '/polityka-prywatnosci' },
  '/resume-examples': { cs: '/priklady-zivotopisu', de: '/lebenslauf-beispiele', pl: '/przyklady-cv' },
  '/resume-synonyms': { cs: '/synonyma-do-zivotopisu', de: '/synonyme-lebenslauf', pl: '/synonimy-do-cv' },
  '/tailor-resume-to-job': { cs: '/zivotopis-na-miru-inzeratu', de: '/lebenslauf-anpassen', pl: '/cv-dopasowane-do-oferty' },
  '/templates': { cs: '/sablony-zivotopisu', de: '/lebenslauf-vorlagen', pl: '/szablony-cv' },
  '/terms': { cs: '/obchodni-podminky', de: '/nutzungsbedingungen', pl: '/regulamin' },
}

/**
 * The pages generated under a hub, by the hub's English path: English slug →
 * the slug in a language. A slug with no translation stays English: the
 * competitors, the German resume examples without German content (only those
 * in data/resume-examples/de have a German slug), and every Polish one: the
 * generated pages have no Polish content yet, so they keep the English slug
 * and stay out of the Polish sitemap (server/api/__sitemap__/urls.ts).
 */
export const LOCALIZED_SLUGS: Record<string, Record<string, Partial<Translations>>> = {
  '/resume-examples': {
    'teacher': { cs: 'ucitel' },
    'registered-nurse': { cs: 'zdravotni-sestra' },
    'student': { cs: 'student' },
    'special-education-teacher': { cs: 'specialni-pedagog' },
    'cna': { cs: 'osetrovatel' },
    'medical-assistant': { cs: 'zdravotnicky-asistent' },
    'dental-assistant': { cs: 'zubni-asistentka' },
    'customer-service': { cs: 'zakaznicka-podpora' },
    'server': { cs: 'cisnik' },
    'bartender': { cs: 'barman' },
    'retail': { cs: 'prodavac' },
    'project-manager': { cs: 'projektovy-manazer', de: 'projektmanager' },
    'sales': { cs: 'obchodnik' },
    'marketing': { cs: 'marketing', de: 'marketing' },
    'human-resources': { cs: 'personalista', de: 'personalwesen' },
    'administrative-assistant': { cs: 'administrativni-asistent', de: 'verwaltungsassistent' },
    'accountant': { cs: 'ucetni', de: 'buchhalter' },
    'software-engineer': { cs: 'softwarovy-inzenyr', de: 'softwareentwickler' },
    'data-analyst': { cs: 'datovy-analytik', de: 'datenanalyst' },
    'internship': { cs: 'staz' },
    'entry-level': { cs: 'bez-praxe' },
    'pharmacy-technician': { cs: 'farmaceuticky-asistent' },
    'caregiver': { cs: 'pecovatel' },
    'electrician': { cs: 'elektrikar', de: 'elektriker' },
    'truck-driver': { cs: 'ridic-kamionu', de: 'lkw-fahrer' },
    'career-change': { cs: 'zmena-oboru', de: 'berufswechsel' },
  },
  '/resume-synonyms': {
    'experienced': { cs: 'zkuseny', de: 'erfahren' },
    'supported': { cs: 'podporoval', de: 'unterstuetzt' },
    'helped': { cs: 'pomahal', de: 'geholfen' },
    'created': { cs: 'vytvoril', de: 'erstellt' },
    'improved': { cs: 'zlepsil', de: 'verbessert' },
    'developed': { cs: 'vyvinul', de: 'entwickelt' },
    'analyzed': { cs: 'analyzoval', de: 'analysiert' },
    'highlighted': { cs: 'upozornil', de: 'hervorgehoben' },
    'focused': { cs: 'soustredeny', de: 'fokussiert' },
    'passionate': { cs: 'nadseny', de: 'leidenschaftlich' },
    'worked': { cs: 'pracoval', de: 'gearbeitet' },
    'learned': { cs: 'naucil-se', de: 'gelernt' },
    'demonstrated': { cs: 'prokazal', de: 'bewiesen' },
    'fostered': { cs: 'pestoval', de: 'gefoerdert' },
    'maintained': { cs: 'udrzoval', de: 'gepflegt' },
    'implemented': { cs: 'implementoval', de: 'implementiert' },
    'contributed': { cs: 'prispel', de: 'beigetragen' },
    'communicated': { cs: 'komunikoval', de: 'kommuniziert' },
    'transformed': { cs: 'transformoval', de: 'transformiert' },
    'presented': { cs: 'prezentoval', de: 'praesentiert' },
    'increased': { cs: 'zvysil', de: 'gesteigert' },
    'utilize': { cs: 'vyuzivam', de: 'nutzen' },
    'hard-working': { cs: 'pracovity', de: 'fleissig' },
    'enhanced': { cs: 'vylepsil', de: 'optimiert' },
    'established': { cs: 'zalozil', de: 'aufgebaut' },
    'responsible': { cs: 'zodpovedny', de: 'verantwortlich' },
    'managed': { cs: 'ridil', de: 'geleitet' },
    'organized': { cs: 'zorganizoval', de: 'organisiert' },
    'identified': { cs: 'identifikoval', de: 'identifiziert' },
    'completed': { cs: 'dokoncil', de: 'abgeschlossen' },
    'reduced': { cs: 'snizil', de: 'reduziert' },
    'ensure': { cs: 'zajistuji', de: 'sicherstellen' },
    'strengthened': { cs: 'posilil', de: 'gestaerkt' },
    'evaluated': { cs: 'hodnotil', de: 'bewertet' },
    'driven': { cs: 'ambiciozni', de: 'ehrgeizig' },
    'designed': { cs: 'navrhl', de: 'entworfen' },
    'assisted': { cs: 'asistoval', de: 'assistiert' },
    'achieved': { cs: 'dosahl', de: 'erreicht' },
    'led': { cs: 'vedl', de: 'gefuehrt' },
    'collaborated': { cs: 'spolupracoval', de: 'zusammengearbeitet' },
  },
  '/affiliate-programs': {
    'ai-resume-builder': { cs: 'ai-tvurce-zivotopisu', de: 'ki-lebenslauf-editor' },
    'cv-builder': { cs: 'tvurce-zivotopisu', de: 'lebenslauf-editor' },
    'recruiters': { cs: 'naborari', de: 'recruiter' },
    'career-coaches': { cs: 'karierni-poradci', de: 'karriere-coaches' },
    'ai-tools': { cs: 'ai-nastroje', de: 'ki-tools' },
    'job-search': { cs: 'hledani-prace', de: 'jobsuche' },
  },
  // Competitor names read the same in every language.
  '/alternatives': {},
}
